import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import posthog, { type Properties } from "posthog-js";
import { APP_VERSION, BUILD_CHANNEL, SENTRY_RELEASE } from "./buildInfo";
import { runtimeEnv } from "./runtimeEnv";

type MonitoringConfig = {
  telemetryEnabled: boolean;
  productAnalyticsEnabled: boolean;
  sessionReplayEnabled: boolean;
  errorMonitoringEnabled: boolean;
  feedbackSurveyEnabled: boolean;
};

type MonitoringContext = {
  anonymousUserId: string | null;
  inviteCohort: string | null;
  runId: string | null;
  screen: string;
  sessionId: string | null;
};

type MonitoringErrorOptions = {
  forwardToSentry?: boolean;
};

type MonitoringSpanOptions = {
  attributes?: Record<string, string | number | boolean>;
  op?: string;
};

const DEFAULT_POSTHOG_API_HOST = "https://us.i.posthog.com";
const DEFAULT_POSTHOG_REPLAY_SAMPLE_RATE = 0.1;
const DEFAULT_SENTRY_TRACES_SAMPLE_RATE = 0.15;
const posthogProjectKey = runtimeEnv.VITE_POSTHOG_KEY?.trim() || null;
const posthogApiHost = runtimeEnv.VITE_POSTHOG_API_HOST?.trim() || DEFAULT_POSTHOG_API_HOST;
const sentryDsn = runtimeEnv.VITE_SENTRY_DSN?.trim() || null;

let monitoringInitialized = false;
let posthogInitialized = false;

let currentConfig: MonitoringConfig = {
  telemetryEnabled: true,
  productAnalyticsEnabled: false,
  sessionReplayEnabled: false,
  errorMonitoringEnabled: false,
  feedbackSurveyEnabled: false
};

let currentContext: MonitoringContext = {
  anonymousUserId: null,
  inviteCohort: null,
  runId: null,
  screen: "title",
  sessionId: null
};

function parseSampleRate(value: string | undefined, fallback: number): number {
  if (value === undefined) {
    return fallback;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.max(0, Math.min(1, parsedValue));
}

function shouldEnableProductAnalytics(): boolean {
  return (
    posthogProjectKey !== null &&
    currentConfig.telemetryEnabled &&
    currentConfig.productAnalyticsEnabled
  );
}

function shouldEnableReplay(): boolean {
  return shouldEnableProductAnalytics() && currentConfig.sessionReplayEnabled;
}

function shouldEnableErrorMonitoring(): boolean {
  return (
    sentryDsn !== null &&
    currentConfig.telemetryEnabled &&
    currentConfig.errorMonitoringEnabled
  );
}

function buildSharedProperties(): Properties {
  return {
    anonymous_user_id: currentContext.anonymousUserId,
    app_version: APP_VERSION,
    build_channel: BUILD_CHANNEL,
    feedback_survey_enabled: currentConfig.feedbackSurveyEnabled,
    invite_cohort: currentContext.inviteCohort,
    run_id: currentContext.runId,
    screen: currentContext.screen,
    session_id: currentContext.sessionId
  };
}

function syncSentryScope(): void {
  Sentry.setUser(
    currentContext.anonymousUserId === null ? null : { id: currentContext.anonymousUserId }
  );
  Sentry.setTag("app_version", APP_VERSION);
  Sentry.setTag("build_channel", BUILD_CHANNEL);
  Sentry.setTag("screen", currentContext.screen);
  Sentry.setTag("feedback_survey_enabled", String(currentConfig.feedbackSurveyEnabled));
  Sentry.setTag("invite_cohort", currentContext.inviteCohort ?? "unknown");
  Sentry.setTag("run_id", currentContext.runId ?? "none");
  Sentry.setTag("session_id", currentContext.sessionId ?? "none");
  Sentry.setContext("telemetry", {
    anonymous_user_id: currentContext.anonymousUserId,
    app_version: APP_VERSION,
    build_channel: BUILD_CHANNEL,
    feedback_survey_enabled: currentConfig.feedbackSurveyEnabled,
    invite_cohort: currentContext.inviteCohort,
    run_id: currentContext.runId,
    screen: currentContext.screen,
    session_id: currentContext.sessionId
  });
}

function syncPostHogState(): void {
  if (!posthogInitialized) {
    return;
  }

  if (shouldEnableProductAnalytics()) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }

  posthog.set_config({
    disable_session_recording: !shouldEnableReplay()
  });

  if (shouldEnableReplay()) {
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
  }

  posthog.register(buildSharedProperties());

  if (currentContext.anonymousUserId !== null) {
    posthog.identify(currentContext.anonymousUserId, {
      invite_cohort: currentContext.inviteCohort ?? undefined
    });
  }
}

export function initializeMonitoring(): void {
  if (monitoringInitialized) {
    return;
  }

  monitoringInitialized = true;

  if (sentryDsn !== null) {
    const tracesSampleRate = parseSampleRate(
      runtimeEnv.VITE_SENTRY_TRACES_SAMPLE_RATE,
      DEFAULT_SENTRY_TRACES_SAMPLE_RATE
    );

    Sentry.init({
      dsn: sentryDsn,
      environment: BUILD_CHANNEL,
      integrations: [
        Sentry.browserTracingIntegration({
          shouldCreateSpanForRequest: (url) => !url.includes("/functions/v1/collect-beta-event")
        })
      ],
      release: SENTRY_RELEASE,
      beforeSend: (event) => (shouldEnableErrorMonitoring() ? event : null),
      tracesSampler: () => (shouldEnableErrorMonitoring() ? tracesSampleRate : 0)
    });

    syncSentryScope();
  }

  if (posthogProjectKey !== null) {
    const replaySampleRate = parseSampleRate(
      runtimeEnv.VITE_POSTHOG_REPLAY_SAMPLE_RATE,
      DEFAULT_POSTHOG_REPLAY_SAMPLE_RATE
    );

    posthog.init(posthogProjectKey, {
      api_host: posthogApiHost,
      autocapture: false,
      capture_pageleave: false,
      capture_pageview: false,
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "#invite-code,[data-sensitive='true']",
        sampleRate: replaySampleRate
      }
    });

    posthogInitialized = true;
    syncPostHogState();
  }
}

export function updateMonitoringConfig(partialConfig: Partial<MonitoringConfig>): void {
  currentConfig = {
    ...currentConfig,
    ...partialConfig
  };

  syncSentryScope();
  syncPostHogState();
}

export function updateMonitoringContext(partialContext: Partial<MonitoringContext>): void {
  currentContext = {
    ...currentContext,
    ...partialContext
  };

  syncSentryScope();
  syncPostHogState();
}

export function trackProductAnalyticsEvent(
  eventName: string,
  properties: Record<string, string | number | boolean | null>
): void {
  if (!posthogInitialized || !shouldEnableProductAnalytics()) {
    return;
  }

  posthog.capture(eventName, properties);
}

export function captureMonitoringError(
  source: string,
  error: unknown,
  details: Record<string, string | number | boolean | null>,
  options: MonitoringErrorOptions = {}
): void {
  if (options.forwardToSentry === false || !shouldEnableErrorMonitoring()) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("error_source", source);

    Object.entries(details).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    Sentry.captureMessage(typeof error === "string" ? error : "Unknown error", "error");
  });
}

export function withMonitoringSpan<T>(
  name: string,
  callback: () => Promise<T>,
  options: MonitoringSpanOptions = {}
): Promise<T> {
  if (!shouldEnableErrorMonitoring()) {
    return callback();
  }

  return Sentry.startSpan(
    {
      name,
      op: options.op ?? "ui.action",
      attributes: {
        app_version: APP_VERSION,
        build_channel: BUILD_CHANNEL,
        ...options.attributes
      }
    },
    callback
  );
}

function ErrorFallback() {
  return (
    <main
      style={{
        alignItems: "center",
        background: "#050811",
        color: "#e9fbff",
        display: "flex",
        inset: 0,
        justifyContent: "center",
        padding: "1.5rem",
        position: "fixed",
        textAlign: "center"
      }}
    >
      <div>
        <h1 style={{ marginBottom: "0.75rem" }}>Tavela Tiles hit an unexpected error.</h1>
        <p style={{ margin: 0, maxWidth: "24rem", opacity: 0.85 }}>
          Reload the page and try again. If it keeps happening after a refresh, use the Support or
          Feedback link from the title screen.
        </p>
      </div>
    </main>
  );
}

export function MonitoringErrorBoundary({ children }: { children: ReactNode }) {
  return <Sentry.ErrorBoundary fallback={<ErrorFallback />}>{children}</Sentry.ErrorBoundary>;
}
